<script setup>
import { ref, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'


const props = defineProps({
  projet:                    { type: Object,  default: () => ({}) },
  estimations:               { type: Array,   default: () => [] },
  bons:                      { type: Array,   default: () => [] },
  type_bon_statuts:          { type: Array,   default: () => [] },
  bons_filtre_code:          { type: String,  default: null },
  can_update:                { type: Boolean, default: false },
  can_delete:                { type: Boolean, default: false },
  can_read_all_estimation:   { type: Boolean, default: false },
  can_create_estimation:     { type: Boolean, default: false },
  can_create_rapport:        { type: Boolean, default: false },
  can_create_bon:            { type: Boolean, default: false },
  can_modify_projet:         { type: Boolean, default: false },
  bon_module:                { type: Boolean, default: false },
  rapports_search_url:       { type: String,  default: null },
})

const bonsFiltre = ref(props.bons_filtre_code)

// Pagination bordereaux
const estPage = ref(1)
const EST_PAGE_SIZE = 5
const estTotalPages = computed(() => Math.ceil(props.estimations.length / EST_PAGE_SIZE))
const estPaged = computed(() => {
  const start = (estPage.value - 1) * EST_PAGE_SIZE
  return props.estimations.slice(start, start + EST_PAGE_SIZE)
})
const estPageNumbers = computed(() => {
  const total = estTotalPages.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const cur = estPage.value
  const pages = new Set([1, total, cur])
  if (cur > 1) pages.add(cur - 1)
  if (cur < total) pages.add(cur + 1)
  return [...pages].sort((a, b) => a - b)
})

const bonsAffiches = computed(() => {
  if (!bonsFiltre.value) return props.bons
  return props.bons.filter(b => b.type_bon_statut_code === bonsFiltre.value)
})

function setFiltre(code) {
  bonsFiltre.value = code || null
}

function confirmDelete() {
  const msg = [
    `Supprimer le projet ${props.projet.code} ?`,
    `${props.projet.count_estimations} estimation(s)`,
    `${props.projet.count_rapports} rapport(s)`,
  ].join('\n')
  if (!confirm(msg)) return
  router.delete(`/projets/${props.projet.id}`)
}

function fmt(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' })
}

</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Projets &rsaquo; {{ projet.code }}
      </div>
    </template>

    <div class="p-8 space-y-8">
      <div class="space-y-8">

        <!-- Page Header -->
        <div class="flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">{{ projet.nom }}</h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">{{ projet.code }}</p>
          </div>
          <div class="flex items-center gap-3">
            <button
              v-if="can_delete"
              @click="confirmDelete"
              class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-error border border-error/30 hover:bg-error/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-base">delete</span>
              Supprimer
            </button>
            <a
              v-if="can_update"
              :href="`/projets/${projet.id}/edit`"
              class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-base">edit</span>
              Modifier
            </a>
            <a
              href="/projets"
              class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-base">arrow_back</span>
              Retour
            </a>
          </div>
        </div>

        <!-- Infos projet -->
        <div class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
          <div class="divide-y divide-outline-variant/10">

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-44 shrink-0">Code</span>
              <span class="font-mono text-sm font-bold text-primary">{{ projet.code }}</span>
            </div>

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-44 shrink-0">Désignation</span>
              <span class="text-sm text-on-surface">{{ projet.nom || '—' }}</span>
            </div>

            <div v-if="projet.description" class="flex items-start px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-44 shrink-0 pt-0.5">Description</span>
              <span class="text-sm text-on-surface/80 whitespace-pre-line">{{ projet.description }}</span>
            </div>

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-44 shrink-0">Chargé de projet</span>
              <span class="text-sm text-on-surface font-bold">{{ projet.charge_nom || '—' }}</span>
            </div>

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-44 shrink-0">Période</span>
              <span class="text-sm text-on-surface">{{ fmt(projet.debut) }} → {{ fmt(projet.fin) }}</span>
            </div>

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-44 shrink-0">Statut</span>
              <span
                :class="projet.actif
                  ? 'bg-tertiary/15 text-tertiary border border-tertiary/30'
                  : 'bg-outline/10 text-outline border border-outline/20'"
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest"
              >
                <span class="w-1.5 h-1.5 rounded-full" :class="projet.actif ? 'bg-tertiary' : 'bg-outline/50'"></span>
                {{ projet.actif ? 'Actif' : 'Inactif' }}
              </span>
            </div>

            <div v-if="can_read_all_estimation" class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-44 shrink-0">Rapports</span>
              <a
                v-if="rapports_search_url"
                :href="rapports_search_url"
                class="text-sm text-primary font-bold hover:brightness-125 transition-all flex items-center gap-1.5"
              >
                <span class="material-symbols-outlined text-base">assessment</span>
                Voir la compilation
              </a>
              <span v-else class="text-sm text-outline italic">Aucun rapport</span>
            </div>

          </div>
        </div>

        <!-- Estimations -->
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">
              {{ estimations.length }} bordereau{{ estimations.length > 1 ? 'x' : '' }} associé{{ estimations.length > 1 ? 's' : '' }}
            </h2>
            <a
              v-if="can_create_estimation"
              :href="`/estimations/new?projet_id=${projet.id}`"
              class="machined-sheen px-4 py-2 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow"
            >
              <span class="material-symbols-outlined text-sm">add_circle</span>
              Nouveau bordereau
            </a>
          </div>
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="border-b border-outline-variant/10">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-32">Code</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Nom</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-32">Soumission</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-32">Acceptation</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Rapports</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="e in estPaged" :key="e.id" class="hover:bg-surface-container-highest/20 transition-colors">
                <td class="px-6 py-3">
                  <a v-if="e.can_show" :href="`/estimations/${e.id}`" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-all">{{ e.code }}</a>
                  <span v-else class="font-mono text-sm font-bold text-primary/50">{{ e.code }}</span>
                </td>
                <td class="px-6 py-3 text-sm text-on-surface">{{ e.nom || '—' }}</td>
                <td class="px-6 py-3 text-xs text-outline">{{ e.description || '—' }}</td>
                <td class="px-6 py-3 text-xs text-outline">{{ fmt(e.date_soumission) }}</td>
                <td class="px-6 py-3 text-xs text-outline">{{ fmt(e.date_acceptation) }}</td>
                <td class="px-6 py-3">
                  <div class="flex flex-col gap-1">
                    <a v-if="e.can_show" :href="e.rapports_url" class="text-xs text-outline hover:text-on-surface transition-colors flex items-center gap-1">
                      <span class="material-symbols-outlined text-xs">list</span> Voir les rapports
                    </a>
                    <a v-if="e.can_show && can_create_rapport" :href="e.new_rapport_url" class="text-xs text-outline hover:text-on-surface transition-colors flex items-center gap-1">
                      <span class="material-symbols-outlined text-xs">add</span> Nouveau rapport
                    </a>
                    <a v-if="e.search_url" :href="e.search_url" class="text-xs text-primary hover:brightness-125 transition-colors flex items-center gap-1">
                      <span class="material-symbols-outlined text-xs">assessment</span> Compilation
                    </a>
                    <a v-if="e.gallery_url" :href="e.gallery_url" class="text-xs text-primary hover:brightness-125 transition-colors flex items-center gap-1">
                      <span class="material-symbols-outlined text-xs">photo_library</span> Galerie photo
                    </a>
                  </div>
                </td>
              </tr>
              <tr v-if="estimations.length === 0">
                <td colspan="6" class="px-6 py-12 text-center text-outline text-sm italic">Aucune estimation</td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination bordereaux -->
          <div v-if="estTotalPages > 1" class="px-6 py-3 flex items-center justify-between border-t border-outline-variant/10">
            <p class="text-xs text-outline font-bold">Page {{ estPage }} / {{ estTotalPages }}</p>
            <div class="flex items-center gap-1">
              <button
                @click="estPage--"
                :disabled="estPage === 1"
                class="px-2 py-1.5 text-xs font-black rounded-md border border-outline-variant/20 text-outline hover:bg-surface-container-highest disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              ><span class="material-symbols-outlined text-sm">chevron_left</span></button>
              <template v-for="(page, i) in estPageNumbers" :key="page">
                <span v-if="i > 0 && page - estPageNumbers[i-1] > 1" class="px-1 text-outline text-xs">…</span>
                <button
                  @click="estPage = page"
                  :class="page === estPage ? 'bg-primary text-on-primary shadow' : 'text-outline hover:bg-surface-container-highest border border-outline-variant/20'"
                  class="min-w-[2rem] px-2 py-1.5 text-xs font-black rounded-md transition-all"
                >{{ page }}</button>
              </template>
              <button
                @click="estPage++"
                :disabled="estPage === estTotalPages"
                class="px-2 py-1.5 text-xs font-black rounded-md border border-outline-variant/20 text-outline hover:bg-surface-container-highest disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              ><span class="material-symbols-outlined text-sm">chevron_right</span></button>
            </div>
          </div>
        </div>

        <!-- Bons -->
        <div v-if="bon_module" class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between gap-4">
            <div class="flex items-center gap-3 flex-wrap">
              <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">
                Bons
                <span class="ml-2 text-outline font-bold">({{ bonsAffiches.length }}<span v-if="bonsFiltre"> / {{ bons.length }}</span>)</span>
              </h2>
              <!-- Filtres statut -->
              <div class="flex items-center gap-1">
                <button
                  @click="setFiltre(null)"
                  :class="!bonsFiltre ? 'bg-primary text-on-primary' : 'text-outline hover:text-on-surface bg-surface-container border border-outline-variant/10'"
                  class="px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-sm transition-all"
                >Tout</button>
                <button
                  v-for="tbs in type_bon_statuts"
                  :key="tbs.code"
                  @click="setFiltre(tbs.code)"
                  :class="bonsFiltre === tbs.code ? 'bg-primary text-on-primary' : 'text-outline hover:text-on-surface bg-surface-container border border-outline-variant/10'"
                  class="px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-sm transition-all"
                >{{ tbs.description }}</button>
              </div>
            </div>
            <div>
              <a
                v-if="can_create_bon && projet.utilisateur_id"
                :href="`/bons/new?projet_id=${projet.id}`"
                class="machined-sheen px-4 py-2 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow"
              >
                <span class="material-symbols-outlined text-sm">add_circle</span>
                Nouveau bon
              </a>
              <a
                v-else-if="can_create_bon && can_modify_projet"
                :href="`/projets/${projet.id}/edit`"
                class="text-xs text-outline hover:text-on-surface transition-colors flex items-center gap-1.5"
              >
                <span class="material-symbols-outlined text-sm">warning</span>
                Assigner un chargé de projet
              </a>
            </div>
          </div>

          <div v-if="bons.length > 0">
            <table class="w-full text-left border-collapse">
              <thead>
                <tr class="border-b border-outline-variant/10">
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24">Numéro</th>
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24">N° TS</th>
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-32">Statut</th>
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Nom</th>
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-32">Type</th>
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-32">Création</th>
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-32">Début</th>
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="b in bonsAffiches" :key="b.id" class="hover:bg-surface-container-highest/20 transition-colors">
                  <td class="px-6 py-3">
                    <a :href="`/bons/${b.id}`" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-all">{{ b.numero_str }}</a>
                  </td>
                  <td class="px-6 py-3 text-xs text-outline font-mono">{{ b.num_ts || '—' }}</td>
                  <td class="px-6 py-3">
                    <span v-if="b.statut" class="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-primary/10 text-primary border border-primary/20">{{ b.statut }}</span>
                    <span v-else class="text-xs text-outline">—</span>
                  </td>
                  <td class="px-6 py-3 text-sm text-on-surface">{{ b.nom || '—' }}</td>
                  <td class="px-6 py-3 text-xs text-outline">{{ b.type_bon || '—' }}</td>
                  <td class="px-6 py-3 text-xs text-outline">{{ fmt(b.created_at) }}</td>
                  <td class="px-6 py-3 text-xs text-outline">{{ fmt(b.debut) }}</td>
                  <td class="px-6 py-3 text-xs text-outline/80">{{ b.description || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="px-6 py-12 text-center text-outline text-sm italic">Aucun bon</div>
        </div>

        <!-- Footer -->
        <footer class="pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
